/**
 * ConfirmDialog — pretty replacement for `window.confirm`.
 *
 * Usage:
 *   const [open, setOpen] = useState(false)
 *   <button onClick={() => setOpen(true)}>刪除</button>
 *   <ConfirmDialog open={open} onOpenChange={setOpen} title="確定刪除？" message="..." onConfirm={handleDelete} tone="danger" />
 *
 * Surfaces onConfirm errors inline (instead of just leaving busy=true forever).
 * Accepts ReactNode for `message` so callers can render a longer body.
 */
import { useState, type ReactNode } from 'react'
import {
  DialogRoot,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from './Dialog'

interface ConfirmDialogProps {
  open: boolean
  onOpenChange: (v: boolean) => void
  title: string
  message?: ReactNode
  confirmLabel?: string
  cancelLabel?: string
  tone?: 'danger' | 'primary'
  onConfirm: () => void | Promise<void>
}

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  message,
  confirmLabel = '確認',
  cancelLabel = '取消',
  tone = 'primary',
  onConfirm,
}: ConfirmDialogProps) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const confirmClass =
    tone === 'danger'
      ? 'bg-danger text-surface hover:bg-danger/90'
      : 'bg-primary text-surface hover:bg-primary-dark'
  return (
    <DialogRoot open={open} onOpenChange={(v) => { if (!busy) onOpenChange(v) }}>
      <DialogHeader>
        <DialogTitle className="text-lg font-bold text-ink">{title}</DialogTitle>
        {message && <DialogDescription className="text-sm text-ink-soft">{message}</DialogDescription>}
      </DialogHeader>
      {error && (
        <p role="alert" className="text-sm text-danger bg-danger/10 rounded-yada-md px-3 py-2 mb-2">
          {error}
        </p>
      )}
      <DialogFooter>
        <DialogClose asChild>
          <button
            type="button"
            disabled={busy}
            className="px-4 py-1.5 rounded-yada-full text-sm font-semibold border-2 border-surface-3 text-ink hover:bg-surface-2 disabled:opacity-50"
          >
            {cancelLabel}
          </button>
        </DialogClose>
        <button
          type="button"
          disabled={busy}
          onClick={async () => {
            setBusy(true)
            setError(null)
            try {
              await onConfirm()
              onOpenChange(false)
            } catch (err) {
              setError(err instanceof Error ? err.message : '操作失敗')
            } finally {
              setBusy(false)
            }
          }}
          className={`px-4 py-1.5 rounded-yada-full text-sm font-semibold disabled:opacity-50 ${confirmClass}`}
        >
          {busy ? '處理中…' : confirmLabel}
        </button>
      </DialogFooter>
    </DialogRoot>
  )
}